import React from 'react';

/*
  Next Steps fallback
  -------------------
  Next Steps copy lives in Quickbase now (see getNextSteps in the backend). This
  is the error path: if that request fails, a DJ still sees the copy that shipped
  with the build rather than an empty panel.

  Lifted verbatim out of ElectionsSection.js, where it was a five-way ternary on
  election_status. Two deliberate notes:

    - The 'Rejected' branch appears unreachable - nothing in the backend emits
      that string, and the terminal statuses are 'Canceled' and 'Not Hired'. It
      is kept as-is because this was a like-for-like migration, not a cleanup.

    - The Sosha sentence is the one thing here that is no longer verbatim. It
      used to name "the Post to social button at the top of the screen" when a
      DJ had no link, which was wrong - Header.js renders that button only when
      dj.sosha_link exists - and the Quickbase path no longer substitutes text
      at all. Both now drop the link and say nothing in its place.

    - The dates below are the hardcoded ones this feature exists to get rid of.
      Don't fix them here; fix them in Quickbase, where {{election_date}} and
      {{election_name}} resolve against the election actually in scope. This file
      is a snapshot of the old behavior, not somewhere to keep authoring copy.

  This is also the provenance record for the seeded Quickbase rows - the seed
  script transcribes the same copy, so the two should be diffable by eye.
*/
const NextStepsFallback = ({ status, dj }) => {
  if (status === 'Rejected') {
    return (
      <div className="text-sm text-gray-600 bg-red-50 p-4 rounded-lg">
        <p className="font-medium text-red-800 mb-2">Thank you for your interest in DJs at the Polls</p>
        <p>Unfortunately, we are unable to move forward with your application at this time. We encourage you to apply for future elections and continue supporting voter engagement in your community.</p>
      </div>
    );
  }

  if (status === 'Hired') {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-green-800 font-medium mb-2">Congratulations! You've been hired to DJ at the Polls for the November election.</p>
          <p className="text-sm text-gray-600 mb-2">We'll send your polling place assignment closer to the election.</p>
          <p className="text-sm text-gray-600">Click below to sign your contract and sign up for a required Zoom info session.</p>
        </div>
        <button
          onClick={() => {
            const contractSection = document.getElementById('contract-section');
            if (contractSection) {
              contractSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
        >
          Go to Contract Details
        </button>
      </div>
    );
  }

  if (status === 'Contract Signed') {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-green-800 font-medium mb-2">Congratulations on joining DJs at the Polls for the Tuesday, November 3rd Election! Next steps:</p>
        </div>
        <ol className="space-y-4">
          <li className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">1</span>
            </div>
            <div className="text-sm text-gray-600">
              <p>Sign up for required info sessions - <a href="https://www.djsatthepolls.org/training" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark underline">here</a></p>
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">2</span>
            </div>
            <div className="text-sm text-gray-600">
              {/*
                A DJ with no Sosha link gets no link and nothing standing in for
                one - the same rule resolveDynamicLinks applies to the Quickbase
                copy. The sentence is split so the instruction survives on its
                own and only the link sentence goes, which is the shape the
                Formatting Guide field asks authors for.
              */}
              <p>
                Announce that you're joining us in a social media post.
                {dj?.sosha_link && (
                  <>
                    {' '}
                    <a href={dj.sosha_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark underline">Find graphics and posts you can use here.</a>
                  </>
                )}
              </p>
            </div>
          </li>
        </ol>
      </div>
    );
  }

  if (status === 'Applied' || status === 'Vetted') {
    return (
      <ol className="space-y-4">
        <li className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">1</span>
          </div>
          <span className="text-sm text-gray-600">We'll review your application</span>
        </li>
        <li className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">2</span>
          </div>
          <span className="text-sm text-gray-600">
            If you're hired, you will receive a text message with further instructions
          </span>
        </li>
        <li className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">3</span>
          </div>
          <span className="text-sm text-gray-600">
          Once you get the text, come back to the portal to sign your contract and sign up for a required Zoom info session
          </span>
        </li>
      </ol>
    );
  }

  return <NextStepsEmpty />;
};

// Also the "no row in Quickbase" state, not just the fallback's default branch -
// a status an admin hasn't authored yet reads the same as one that genuinely has
// nothing pending.
export const NextStepsEmpty = () => (
  <div className="text-sm text-gray-600 p-4 bg-blue-50 rounded-lg">
    <p className="font-medium text-blue-800 mb-2">You have no next steps</p>
    <p>Please check back later for updates.</p>
  </div>
);

export default NextStepsFallback;
